import type { DefaultStatusValueModel } from './competitor-engagement-statuses';

/**
 * PD-17604 — where a person or company came from (campaigns v2,
 * docs/claude/campaigns/03-data-model.md "Lead source and creation source").
 *
 * LEAD SOURCE is a tenant-editable pick list (catalog type `lead_source`,
 * Settings → Catalog Values; no free typing, Chris 10/26/26) whose values
 * each mean one of the fixed meanings below, so reports count by meaning
 * whatever the tenant calls its values. Three meanings name one more thing
 * (the show, who referred them, the campaign).
 *
 * CREATION SOURCE is how the record was made, set by the system on every new
 * person and company and never typed, so attribution can be trusted.
 */

export const LEAD_SOURCE_MEANINGS = [
	'trade_show',
	'referral',
	'website',
	'campaign',
	'cold_outreach',
	'walk_in',
	'customer_visit',
	'partner',
	'other',
] as const;
export type LeadSourceMeaning = (typeof LEAD_SOURCE_MEANINGS)[number];

export const LEAD_SOURCE_MEANING_LABELS: Readonly<Record<LeadSourceMeaning, string>> = {
	trade_show: 'Trade Show',
	referral: 'Referral',
	website: 'Website',
	campaign: 'Email Campaign',
	cold_outreach: 'Cold Outreach',
	walk_in: 'Walk-In',
	customer_visit: 'Customer Visit',
	partner: 'Partner',
	other: 'Other',
};

/** Seeded per tenant (title case, Chris 10/26/26). */
export const DEFAULT_LEAD_SOURCES: ReadonlyArray<DefaultStatusValueModel> = [
	{ name: 'Trade Show', outcome: 'trade_show' },
	{ name: 'Referral', outcome: 'referral' },
	{ name: 'Website', outcome: 'website' },
	{ name: 'Email Campaign', outcome: 'campaign' },
	{ name: 'Cold Call', outcome: 'cold_outreach' },
	{ name: 'Walk-In', outcome: 'walk_in' },
	{ name: 'Customer Visit', outcome: 'customer_visit' },
	{ name: 'Partner', outcome: 'partner' },
	{ name: 'Other', outcome: 'other' },
];

/** The record a lead source's one detail points at (`leadSourceRefType`). */
export const LEAD_SOURCE_REF_TYPES = ['trade_show', 'contact', 'client', 'campaign'] as const;
export type LeadSourceRefType = (typeof LEAD_SOURCE_REF_TYPES)[number];

/**
 * Which detail each meaning may name: the show for a trade show, the person
 * or company who referred them, the campaign. The other meanings name none.
 * The trade show detail is offered once trade shows exist (PD-17632).
 */
export const LEAD_SOURCE_DETAIL_REF_TYPES: Readonly<
	Partial<Record<LeadSourceMeaning, ReadonlyArray<LeadSourceRefType>>>
> = {
	trade_show: ['trade_show'],
	referral: ['contact', 'client'],
	campaign: ['campaign'],
};

/** May a lead source with this meaning name a detail of this type? */
export function isLeadSourceRefAllowed(
	meaning: string | null | undefined,
	refType: string | null | undefined,
): boolean {
	if (refType == null) {
		return true;
	}

	const allowed = LEAD_SOURCE_DETAIL_REF_TYPES[meaning as LeadSourceMeaning] ?? [];

	return (allowed as ReadonlyArray<string>).includes(refType);
}

/** How a person or company record was made. Set by the system only. */
export const CREATION_SOURCES = [
	'manual',
	'web_form',
	'portal_registration',
	'import',
	'erp_sync',
	'api',
	'apollo',
	'card_scan',
	'booking_page',
	'lead_finder',
	'prospecting',
] as const;
export type CreationSource = (typeof CREATION_SOURCES)[number];

export const CREATION_SOURCE_LABELS: Readonly<Record<CreationSource, string>> = {
	manual: 'Added by hand',
	web_form: 'Web form',
	portal_registration: 'Portal registration',
	import: 'Import',
	erp_sync: 'ERP sync',
	api: 'API',
	apollo: 'Apollo',
	card_scan: 'Card scan',
	booking_page: 'Booking page',
	lead_finder: 'Lead Finder',
	prospecting: 'Google prospecting',
};

/**
 * The creation source a stored value means. Values written before PD-17604
 * stay as they were (`csv_import`, `form:<formKey>`) and read as their
 * current code; null when nothing was recorded (rows written outside Workd's
 * own create paths, or before creation sources existed).
 */
export function normalizeCreationSource(raw: string | null | undefined): CreationSource | null {
	const value = raw?.trim() ?? '';

	if (value === '') {
		return null;
	}

	if (value === 'csv_import') {
		return 'import';
	}

	if (value === 'form:registration') {
		return 'portal_registration';
	}

	if (value.startsWith('form:')) {
		return 'web_form';
	}

	return (CREATION_SOURCES as ReadonlyArray<string>).includes(value) ? (value as CreationSource) : null;
}

/** The creation source a form submission's records get. */
export function creationSourceForForm(formKey: string): CreationSource {
	return formKey === 'registration' ? 'portal_registration' : 'web_form';
}
